import { httpClient } from '../core/http';
import { env } from '../config/env';
import {
  BillingTransaction,
  BillingTransactionsResponse,
  CancelSubscriptionRequest,
  ChangePlanRequest,
  PurchaseResponse,
  SubscriptionActionResponse,
  SubscriptionResponse,
  TokenPackage,
} from '../types/entities';

/**
 * Serviço para gerenciar operações de billing:
 * - listagem de planos
 * - checkout
 * - assinatura recorrente (status, troca e cancelamento)
 * - histórico de transações
 */
export class BillingService {
  private basePath = '/v1/billing';
  private isStripeTestModeEnabled(): boolean {
    const envFlag = env.REACT_APP_STRIPE_TEST_MODE;

    if (envFlag === 'true') {
      return true;
    }

    if (envFlag === 'false') {
      return false;
    }

    return process.env.NODE_ENV !== 'production';
  }

  /**
   * Normaliza payloads de pacotes para um array uniforme.
   * A API pode retornar formatos diferentes dependendo da versão/ambiente.
   */
  private extractPackagesFromPayload(payload: unknown): TokenPackage[] {
    if (Array.isArray(payload)) {
      return payload as TokenPackage[];
    }

    if (!payload || typeof payload !== 'object') {
      return [];
    }

    const data = payload as Record<string, unknown>;

    if (Array.isArray(data.items)) {
      return data.items as TokenPackage[];
    }

    if (Array.isArray(data.packages)) {
      return data.packages as TokenPackage[];
    }

    if (Array.isArray(data.data)) {
      return data.data as TokenPackage[];
    }

    if (data.data && typeof data.data === 'object') {
      const nestedData = data.data as Record<string, unknown>;

      if (Array.isArray(nestedData.items)) {
        return nestedData.items as TokenPackage[];
      }

      if (Array.isArray(nestedData.packages)) {
        return nestedData.packages as TokenPackage[];
      }
    }

    return [];
  }

  /**
   * Busca todos os pacotes de tokens disponíveis
   * @returns Array de pacotes de tokens ativos
   */
  async getPackages(): Promise<TokenPackage[]> {
    const response = await httpClient.get<unknown>(`${this.basePath}/packages`);

    const packages = this.extractPackagesFromPayload(response.data);

    // Filtrar apenas pacotes ativos
    return packages.filter(pkg => pkg.is_active);
  }

  /**
   * Realiza a compra de um pacote de tokens
   * @param packageId ID do pacote a ser comprado
   * @param currency Código da moeda (ex: USD, BRL, EUR)
   * @returns Resposta da compra com checkout_url do Stripe
   */
  async purchasePackage(
    packageId: number,
    currency: string
  ): Promise<PurchaseResponse> {
    const useStripeTestMode = this.isStripeTestModeEnabled();
    const response = await httpClient.post<PurchaseResponse>(
      `${this.basePath}/purchase`,
      { package_id: packageId, currency },
      useStripeTestMode
        ? {
            headers: {
              'X-Stripe-Mode': 'test',
              'X-Stripe-Test-Mode': 'true',
            },
          }
        : undefined
    );
    return response.data;
  }

  /**
   * Busca a assinatura atual do usuário autenticado.
   */
  async getSubscription(): Promise<SubscriptionResponse> {
    const response = await httpClient.get<SubscriptionResponse>(
      `${this.basePath}/subscription`
    );
    return response.data;
  }

  /**
   * Troca o plano da assinatura recorrente.
   */
  async changePlan(
    payload: ChangePlanRequest
  ): Promise<SubscriptionActionResponse> {
    const response = await httpClient.post<SubscriptionActionResponse>(
      `${this.basePath}/subscription/change-plan`,
      payload
    );
    return response.data;
  }

  /**
   * Cancela a assinatura recorrente.
   */
  async cancelSubscription(
    payload: CancelSubscriptionRequest = {}
  ): Promise<SubscriptionActionResponse> {
    const response = await httpClient.post<SubscriptionActionResponse>(
      `${this.basePath}/subscription/cancel`,
      payload
    );
    return response.data;
  }

  /**
   * Lista transações/cobranças de billing.
   */
  async getTransactions(params?: {
    page?: number;
    page_size?: number;
  }): Promise<BillingTransactionsResponse> {
    const response = await httpClient.get<
      BillingTransactionsResponse | BillingTransaction[]
    >(`${this.basePath}/transactions`, {
      params,
    });

    if (Array.isArray(response.data)) {
      return {
        items: response.data,
        total: response.data.length,
        page: params?.page || 1,
        page_size: params?.page_size || response.data.length,
      };
    }

    return response.data;
  }
}

export const billingService = new BillingService();
